/** @format — tracking.summary cards matching Tracking chat panel layout */

import React from "react";
import StatusBadge from "@/components/StatusBadge";

function formatEta(etaDate) {
  // Reject non-scalar input so an eta object never renders as "[object Object]".
  if (!etaDate || typeof etaDate === "object") return "—";
  try {
    const d = new Date(etaDate);
    if (Number.isNaN(d.getTime())) return etaDate;
    const diff = Math.ceil((d - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff > 0 && diff < 60) return `${diff} days`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

export default function TrackingDetailCards({ props = {} }) {
  const vessel = props.vesselName ?? props.vessel ?? "—";
  const title = props.vehicle ?? "Shipment";
  const vin = props.vin ?? props.bookingRef ?? props.shipmentRef ?? "";
  const progress =
    props.progressPercent ??
    props.progress?.percent ??
    0;
  const origin = props.origin ?? "—";
  const destination = props.destination ?? "—";
  // The backend resolves this from the carrier / AIS static data and sends a
  // neutral "Vessel" when it genuinely can't tell. This was the literal string
  // "Container Ship", which is wrong for the RoRo car carriers we actually ship
  // on — a claim about how the vehicle is stowed, not a cosmetic label.
  const vesselType = props.vesselType ?? "Vessel";
  const eta = formatEta(props.etaDate);

  return (
    <div className="flex flex-col gap-2 py-4 w-full">
      <div
        className="flex flex-col gap-4 p-2 rounded-lg bg-white border border-[#e4e7ec] w-full"
        style={{ boxShadow: "0px 1px 4px 0 rgba(12,12,13,0.05)" }}
      >
        <div className="flex gap-2 justify-between items-start w-full">
          <div className="flex flex-col flex-1 min-w-0">
            <p className="text-base font-semibold text-[#1d2739] truncate">{vessel}</p>
            <p className="text-[10px] text-[#667185]">{vesselType}</p>
          </div>
          <div className="flex flex-col flex-shrink-0 items-center">
            <p className="text-sm font-semibold text-[#1d2739]">{eta}</p>
            <p className="text-[10px] font-semibold uppercase text-[#667185]">ETA</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 p-2 rounded-lg bg-white border border-[#e4e7ec] w-full">
        <div className="flex gap-2 justify-between items-center min-w-0">
          <p className="text-[10px] font-semibold uppercase text-neutral-500">Shipment</p>
          <StatusBadge status={props.status} className="max-w-[55%] sm:max-w-none" />
        </div>
        <p className="text-sm font-medium text-[#1d2739]">{title}</p>
        {vin ? <p className="text-[10px] text-[#667185]">{vin}</p> : null}
      </div>

      <div className="flex flex-col gap-2 p-2 rounded-lg bg-white border border-[#e4e7ec] w-full">
        <div className="flex justify-between items-center">
          <p className="text-[10px] font-semibold uppercase text-neutral-500">Route Progress</p>
          <p className="text-sm font-semibold text-blue-700">{Math.round(progress)}%</p>
        </div>
        <div className="h-1.5 rounded-full bg-[#D4D4D4] overflow-hidden">
          <div
            className="h-full bg-[#2563EB] rounded-full transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
        <div className="flex justify-between gap-2">
          <p className="text-[10px] text-[#344054] truncate pr-2">{origin}</p>
          <p className="text-[10px] text-[#344054] truncate pl-2 text-right">{destination}</p>
        </div>
      </div>
    </div>
  );
}
